// modules/Battle/BattleUIMenu.js
// 战斗UI菜单模块 - 负责主菜单、技能菜单、道具菜单

import { isClickInRect, applySelectionHighlight } from './BattleUtils.js';
import { BATTLE_MENU_LAYOUT, getBattleMenuLayout } from './BattleMenuLayout.js';
import UIStyleHelper from '../UIStyleHelper.js';

export default class BattleUIMenu {
    constructor(scene) {
        this.scene = scene;
        this.elements = {
            menu: {},
            skillMenu: {},
            itemMenu: {}
        };
        
        this.selectedOption = 0;
        // 人类菜单选项
        this.humanOptions = ['攻击', '防御', '技能', '道具', '上车', '逃跑'];
        // 战车菜单选项
        this.vehicleOptions = ['主炮', '副炮', 'SE武器', '工具', '战术', '下车', '撤退'];
        // 当前使用的菜单选项（根据玩家状态动态切换）
        this.battleOptions = this.humanOptions;
        
        // 技能选择相关
        this.selectingSkill = false;
        this.selectedSkillIndex = 0;
        this.skillList = [];
        this.skillMenuVisible = false;
        
        // 道具选择相关
        this.selectingItem = false;
        this.selectedItemIndex = 0;
        this.itemList = [];
        this.itemMenuVisible = false;
        
        // 武器选择相关（战车武器子菜单）
        this.selectingWeapon = false;
        this.selectedWeaponIndex = 0;
        this.weaponList = [];
        this.weaponMenuVisible = false;
        this.weaponType = null; // 'mainCannon', 'subCannon', 'se'
    }
    
    /**
     * 检查是否可以交互（等待队友操作时禁用）
     * @returns {boolean} 是否可以交互
     */
    canInteract() {
        // 检查是否在等待队友操作
        if (this.scene.battleState && this.scene.battleState.phase === 'waitingForTeamMember') {
            return false;
        }
        
        // 检查UI是否可交互
        if (this.scene.battleUI && !this.scene.battleUI.isInteractive()) {
            return false;
        }
        
        return true;
    }
    
    /**
     * 创建主菜单（底部横排，支持触控）
     */
    createMenu() {
        this.elements.menu.optionTexts = [];
        this.updateMenuOptions();
    }

    /**
     * 绘制底部横排主菜单选项
     */
    _renderMainMenuPanel() {
        if (this.elements.menu.hintText) {
            this.elements.menu.hintText.destroy();
        }
        this.elements.menu.optionTexts.forEach((text) => text?.destroy?.());
        this.elements.menu.optionTexts = [];

        const layout = getBattleMenuLayout(this.scene, this.battleOptions.length);

        this.battleOptions.forEach((option, index) => {
            const text = this._createMainMenuOption(option, index, layout);
            this.elements.menu.optionTexts.push(text);
        });

        this.elements.menu.hintText = this.scene.add.text(
            this.scene.cameras.main.width / 2,
            layout.hintY,
            BATTLE_MENU_LAYOUT.HINT,
            {
                fontSize: '11px',
                fill: '#888888',
                fontFamily: 'monospace'
            }
        ).setOrigin(0.5, 0.5);
        this.elements.menu.hintText.setScrollFactor(0);
        this.elements.menu.hintText.setDepth(901);
    }

    /**
     * @param {string} option
     * @param {number} index
     * @param {Object} layout
     * @returns {Phaser.GameObjects.Text}
     */
    _createMainMenuOption(option, index, layout) {
        const x = layout.startX + index * layout.spacing;
        const text = this.scene.add.text(x, layout.optionY, option, {
            fontSize: BATTLE_MENU_LAYOUT.FONT_SIZE,
            fill: '#cccccc',
            fontFamily: 'monospace',
            fontStyle: 'bold'
        });
        text.setOrigin(0.5, 0.5);
        text.setScrollFactor(0);
        text.setDepth(902);
        text.setInteractive({ useHandCursor: true });

        const hitW = Math.max(layout.spacing - 8, 48);
        const hitH = 36;
        text.hitArea = new Phaser.Geom.Rectangle(-hitW / 2, -hitH / 2, hitW, hitH);
        text.input.hitAreaCallback = Phaser.Geom.Rectangle.Contains;

        text.on('pointerdown', () => {
            if (!this.canInteract()) return;
            this.selectedOption = index;
            this.updateOptionSelection();
            if (this.scene.battleUI?.events) {
                this.scene.battleUI.events.emit('actionSelected', option);
            }
        });

        return text;
    }
    
    /**
     * 更新菜单选项（根据玩家是否在战车中）
     */
    updateMenuOptions() {
        const player = this.scene.battlePlayer?.getPlayer();
        const inVehicle = player?.inVehicle && player?.vehicle;
        
        // 根据玩家状态切换菜单
        this.battleOptions = inVehicle ? this.vehicleOptions : this.humanOptions;
        
        // 重置选择索引
        if (this.selectedOption >= this.battleOptions.length) {
            this.selectedOption = 0;
        }
        
        // 更新菜单显示
        this.refreshMenuDisplay();
    }
    
    /**
     * 刷新菜单显示
     */
    refreshMenuDisplay() {
        if (!this.elements.menu.optionTexts) return;
        this._renderMainMenuPanel();
        this.updateOptionSelection();
    }
    
    /**
     * 更新选项选择
     */
    updateOptionSelection() {
        // 检查是否可以交互（等待队友操作时不显示选中状态）
        const canInteract = this.canInteract();
        
        this.elements.menu.optionTexts.forEach((text, index) => {
            if (canInteract && index === this.selectedOption) {
                text.setFill(UIStyleHelper.MENU_COLORS.SELECTED).setScale(1.1);
            } else {
                // 不可交互或不是选中项，显示灰色普通状态
                text.setFill('#cccccc').setScale(1);
            }
        });
    }
    
    /**
     * 移动选择（横排：-1 左, 1 右）
     * @param {number} direction
     */
    moveSelection(direction) {
        const newIndex = this.selectedOption + direction;
        
        if (newIndex >= 0 && newIndex < this.battleOptions.length) {
            this.selectedOption = newIndex;
            this.updateOptionSelection();
        }
    }
    
    /**
     * 获取选中的选项
     * @returns {string} 选中的选项
     */
    getSelectedOption() {
        return this.battleOptions[this.selectedOption];
    }
    
    /**
     * 创建技能菜单
     */
    createSkillMenu() {
        const screenHeight = this.scene.cameras.main.height;
        const screenWidth = this.scene.cameras.main.width;
        
        // 技能菜单背景
        this.elements.skillMenu.background = this.scene.add.graphics();
        this.elements.skillMenu.background.fillStyle(0x1a1a2e, 0.95);
        this.elements.skillMenu.background.fillRect(
            screenWidth * 0.3,
            screenHeight * 0.2,
            screenWidth * 0.4,
            screenHeight * 0.5
        );
        
        this.elements.skillMenu.border = this.scene.add.graphics();
        this.elements.skillMenu.border.lineStyle(3, 0x4cc9f0, 1);
        this.elements.skillMenu.border.strokeRect(
            screenWidth * 0.3,
            screenHeight * 0.2,
            screenWidth * 0.4,
            screenHeight * 0.5
        );
        
        // 技能菜单标题
        this.elements.skillMenu.title = this.scene.add.text(
            screenWidth * 0.5,
            screenHeight * 0.25,
            '选择技能',
            {
                fontSize: '20px',
                fill: '#4cc9f0',
                fontFamily: 'monospace',
                fontWeight: 'bold'
            }
        ).setOrigin(0.5);
        
        // Cost显示
        this.elements.skillMenu.costText = this.scene.add.text(
            screenWidth * 0.5,
            screenHeight * 0.65,
            '',
            {
                fontSize: '16px',
                fill: '#ffff00',
                fontFamily: 'monospace'
            }
        ).setOrigin(0.5);
        
        this.elements.skillMenu.skillTexts = [];
        
        // 创建点击空白处关闭的区域（覆盖整个屏幕，但低于菜单）
        this.elements.skillMenu.closeArea = this.scene.add.graphics();
        this.elements.skillMenu.closeArea.fillStyle(0x000000, 0.01); // 几乎透明
        this.elements.skillMenu.closeArea.fillRect(0, 0, screenWidth, screenHeight);
        this.elements.skillMenu.closeArea.setScrollFactor(0);
        this.elements.skillMenu.closeArea.setDepth(9998); // 低于菜单但高于其他元素
        this.elements.skillMenu.closeArea.setInteractive(
            new Phaser.Geom.Rectangle(0, 0, screenWidth, screenHeight),
            Phaser.Geom.Rectangle.Contains
        );
        this.elements.skillMenu.closeArea.on('pointerdown', (pointer) => {
            const menuX = screenWidth * 0.3;
            const menuY = screenHeight * 0.2;
            const menuWidth = screenWidth * 0.4;
            const menuHeight = screenHeight * 0.5;
            
            // 点击在菜单区域外时关闭菜单
            if (!isClickInRect(pointer.x, pointer.y, menuX, menuY, menuWidth, menuHeight)) {
                this.selectingSkill = false;
                this.hideSkillMenu();
                if (this.scene.battleUI && this.scene.battleUI.events) {
                    this.scene.battleUI.events.emit('cancelSkillSelection');
                }
            }
        });
        
        // 初始隐藏技能菜单
        this.hideSkillMenu();
    }
    
    /**
     * 显示技能选择
     * @param {Array} skills - 技能列表
     * @param {number} currentCost - 当前Cost
     * @param {number} maxCost - 最大Cost
     */
    showSkillSelection(skills, currentCost, maxCost) {
        this.selectingSkill = true;
        this.skillList = skills || [];
        this.selectedSkillIndex = 0;
        
        this.updateSkillMenu(skills, currentCost, maxCost);
        this.showSkillMenu();
    }
    
    /**
     * 更新技能菜单
     * @param {Array} skills - 技能列表
     * @param {number} currentCost - 当前Cost
     * @param {number} maxCost - 最大Cost
     */
    updateSkillMenu(skills, currentCost, maxCost) {
        // 清除旧的技能文本
        this.elements.skillMenu.skillTexts.forEach(text => {
            if (text && text.destroy) text.destroy();
        });
        this.elements.skillMenu.skillTexts = [];
        
        if (!skills || skills.length === 0) {
            const noSkillText = this.scene.add.text(
                this.scene.cameras.main.width * 0.5,
                this.scene.cameras.main.height * 0.4,
                '没有可用技能',
                {
                    fontSize: '16px',
                    fill: '#888888',
                    fontFamily: 'monospace'
                }
            ).setOrigin(0.5);
            this.elements.skillMenu.skillTexts.push(noSkillText);
            return;
        }
        
        const screenWidth = this.scene.cameras.main.width;
        const screenHeight = this.scene.cameras.main.height;
        const startY = screenHeight * 0.3;
        const spacing = 35;
        
        // 显示技能列表
        skills.forEach((skill, index) => {
            const y = startY + (index * spacing);
            
            // S-E技能不需要Cost，检查弹药
            let canUse = false;
            let skillText = '';
            
            if (skill.type === 'vehicle_se') {
                // S-E技能：显示弹药消耗
                const ammoText = skill.weapon?.ammoConsumption || 1;
                skillText = `${skill.name} - 消耗: ${ammoText}发${skill.weapon?.ammoType || '弹药'}`;
                // S-E技能可用性在BattleActionHandler中检查
                canUse = true; // 先设为true，实际检查在handleSkillSelection中
            } else {
                // 普通技能：检查Cost
                canUse = currentCost >= (skill.cost || 0);
                skillText = `${skill.name} - 消耗: ${skill.cost || 0} Cost`;
            }
            
            const textColor = canUse ? '#ffffff' : '#888888';
            
            const text = this.scene.add.text(
                screenWidth * 0.35,
                y,
                skillText,
                {
                    fontSize: '16px',
                    fill: textColor,
                    fontFamily: 'monospace'
                }
            );
            
            // 设置为可点击（移动端支持）
            if (canUse) {
                text.setInteractive({ useHandCursor: true });
                text.on('pointerdown', () => {
                    // 检查是否可以交互
                    if (!this.canInteract()) {
                        return;
                    }
                    
                    // 设置选中项并触发选择事件
                    this.selectedSkillIndex = index;
                    this.updateSkillMenu(skills, currentCost, maxCost);
                    // 触发事件
                    if (this.scene.battleUI && this.scene.battleUI.events) {
                        const selectedSkill = skills[index];
                        this.scene.battleUI.events.emit('skillSelected', selectedSkill, index);
                    }
                });
            }
            
            // 应用选中项高亮样式
            applySelectionHighlight(text, index === this.selectedSkillIndex, canUse);
            
            this.elements.skillMenu.skillTexts.push(text);
        });
        
        // 更新Cost显示
        if (this.elements.skillMenu.costText) {
            this.elements.skillMenu.costText.setText(`当前Cost: ${currentCost}/${maxCost}`);
        }
    }
    
    /**
     * 移动技能选择
     * @param {number} direction - 方向 (-1 上, 1 下)
     */
    moveSkillSelection(direction) {
        if (this.skillList.length === 0) return;
        
        const newIndex = this.selectedSkillIndex + direction;
        if (newIndex >= 0 && newIndex < this.skillList.length) {
            this.selectedSkillIndex = newIndex;
            const player = this.scene.battlePlayer?.getPlayer();
            this.updateSkillMenu(
                this.skillList,
                player?.cost || 0,
                player?.maxCost || 100
            );
        }
    }
    
    /**
     * 获取选中的技能
     * @returns {Object|null} 选中的技能
     */
    getSelectedSkill() {
        if (this.skillList.length === 0) return null;
        return this.skillList[this.selectedSkillIndex];
    }
    
    /**
     * 显示技能菜单
     */
    showSkillMenu() {
        if (this.elements.skillMenu.background) {
            this.elements.skillMenu.background.setVisible(true);
        }
        if (this.elements.skillMenu.border) {
            this.elements.skillMenu.border.setVisible(true);
        }
        if (this.elements.skillMenu.title) {
            this.elements.skillMenu.title.setVisible(true);
        }
        if (this.elements.skillMenu.costText) {
            this.elements.skillMenu.costText.setVisible(true);
        }
        if (this.elements.skillMenu.closeArea) {
            this.elements.skillMenu.closeArea.setVisible(true);
        }
        this.elements.skillMenu.skillTexts.forEach(text => {
            if (text) text.setVisible(true);
        });
        this.skillMenuVisible = true;
    }
    
    /**
     * 隐藏技能菜单
     */
    hideSkillMenu() {
        if (this.elements.skillMenu.background) {
            this.elements.skillMenu.background.setVisible(false);
        }
        if (this.elements.skillMenu.border) {
            this.elements.skillMenu.border.setVisible(false);
        }
        if (this.elements.skillMenu.title) {
            this.elements.skillMenu.title.setVisible(false);
        }
        if (this.elements.skillMenu.costText) {
            this.elements.skillMenu.costText.setVisible(false);
        }
        if (this.elements.skillMenu.closeArea) {
            this.elements.skillMenu.closeArea.setVisible(false);
        }
        this.elements.skillMenu.skillTexts.forEach(text => {
            if (text) text.setVisible(false);
        });
        this.skillMenuVisible = false;
    }
    
    /**
     * 创建道具菜单
     */
    createItemMenu() {
        const screenHeight = this.scene.cameras.main.height;
        const screenWidth = this.scene.cameras.main.width;
        
        // 道具菜单背景
        this.elements.itemMenu.background = this.scene.add.graphics();
        this.elements.itemMenu.background.fillStyle(0x1a1a2e, 0.95);
        this.elements.itemMenu.background.fillRect(
            screenWidth * 0.3,
            screenHeight * 0.2,
            screenWidth * 0.4,
            screenHeight * 0.5
        );
        
        this.elements.itemMenu.border = this.scene.add.graphics();
        this.elements.itemMenu.border.lineStyle(3, 0xffaa00, 1);
        this.elements.itemMenu.border.strokeRect(
            screenWidth * 0.3,
            screenHeight * 0.2,
            screenWidth * 0.4,
            screenHeight * 0.5
        );
        
        // 道具菜单标题
        this.elements.itemMenu.title = this.scene.add.text(
            screenWidth * 0.5,
            screenHeight * 0.25,
            '选择道具',
            {
                fontSize: '20px',
                fill: '#ffaa00',
                fontFamily: 'monospace',
                fontWeight: 'bold'
            }
        ).setOrigin(0.5);
        
        // 道具描述
        this.elements.itemMenu.descriptionText = this.scene.add.text(
            screenWidth * 0.5,
            screenHeight * 0.65,
            '',
            {
                fontSize: '14px',
                fill: '#aaaaaa',
                fontFamily: 'monospace',
                wordWrap: { width: screenWidth * 0.35 },
                align: 'center'
            }
        ).setOrigin(0.5);
        
        this.elements.itemMenu.itemTexts = [];
        
        // 创建点击空白处关闭的区域（覆盖整个屏幕，但低于菜单）
        this.elements.itemMenu.closeArea = this.scene.add.graphics();
        this.elements.itemMenu.closeArea.fillStyle(0x000000, 0.01); // 几乎透明
        this.elements.itemMenu.closeArea.fillRect(0, 0, screenWidth, screenHeight);
        this.elements.itemMenu.closeArea.setScrollFactor(0);
        this.elements.itemMenu.closeArea.setDepth(9998); // 低于菜单但高于其他元素
        this.elements.itemMenu.closeArea.setInteractive(
            new Phaser.Geom.Rectangle(0, 0, screenWidth, screenHeight),
            Phaser.Geom.Rectangle.Contains
        );
        this.elements.itemMenu.closeArea.on('pointerdown', (pointer) => {
            const menuX = screenWidth * 0.3;
            const menuY = screenHeight * 0.2;
            const menuWidth = screenWidth * 0.4;
            const menuHeight = screenHeight * 0.5;
            
            // 点击在菜单区域外时关闭菜单
            if (!isClickInRect(pointer.x, pointer.y, menuX, menuY, menuWidth, menuHeight)) {
                this.selectingItem = false;
                this.hideItemMenu();
                if (this.scene.battleUI && this.scene.battleUI.events) {
                    this.scene.battleUI.events.emit('cancelItemSelection');
                }
            }
        });
        
        // 初始隐藏道具菜单
        this.hideItemMenu();
    }
    
    /**
     * 显示道具选择
     * @param {Array} items - 道具列表
     */
    showItemSelection(items) {
        this.selectingItem = true;
        this.itemList = items || [];
        this.selectedItemIndex = 0;
        
        this.updateItemMenu(items);
        this.showItemMenu();
    }
    
    /**
     * 更新道具菜单
     * @param {Array} items - 道具列表
     */
    updateItemMenu(items) {
        // 清除旧的道具文本
        this.elements.itemMenu.itemTexts.forEach(text => {
            if (text && text.destroy) text.destroy();
        });
        this.elements.itemMenu.itemTexts = [];
        
        if (!items || items.length === 0) {
            const noItemText = this.scene.add.text(
                this.scene.cameras.main.width * 0.5,
                this.scene.cameras.main.height * 0.4,
                '没有可用道具',
                {
                    fontSize: '16px',
                    fill: '#888888',
                    fontFamily: 'monospace'
                }
            ).setOrigin(0.5);
            this.elements.itemMenu.itemTexts.push(noItemText);
            
            if (this.elements.itemMenu.descriptionText) {
                this.elements.itemMenu.descriptionText.setText('');
            }
            return;
        }
        
        const screenWidth = this.scene.cameras.main.width;
        const screenHeight = this.scene.cameras.main.height;
        const startY = screenHeight * 0.3;
        const spacing = 35;
        
        // 显示道具列表
        items.forEach((item, index) => {
            const y = startY + (index * spacing);
            const canUse = this.scene.battleLogic ? 
                this.scene.battleLogic.canUseItemInBattle(item, this.scene.battlePlayer.getPlayer()) : true;
            
            // 道具名称和数量
            const itemText = `${item.name} x${item.count || 1}`;
            const textColor = canUse ? '#ffffff' : '#888888';
            
            const text = this.scene.add.text(
                screenWidth * 0.35,
                y,
                itemText,
                {
                    fontSize: '16px',
                    fill: textColor,
                    fontFamily: 'monospace'
                }
            );
            
            // 设置为可点击（移动端支持）
            if (canUse) {
                text.setInteractive({ useHandCursor: true });
                text.on('pointerdown', () => {
                    // 检查是否可以交互
                    if (!this.canInteract()) {
                        return;
                    }
                    
                    // 设置选中项并触发选择事件
                    this.selectedItemIndex = index;
                    this.updateItemMenu(items);
                    // 触发事件
                    if (this.scene.battleUI && this.scene.battleUI.events) {
                        const selectedItem = items[index];
                        this.scene.battleUI.events.emit('itemSelected', selectedItem, index);
                    }
                });
            }
            
            // 应用选中项高亮样式
            applySelectionHighlight(text, index === this.selectedItemIndex, canUse);
            
            // 如果选中项，显示道具描述
            if (index === this.selectedItemIndex && this.elements.itemMenu.descriptionText) {
                const description = this.getItemDescription(item);
                this.elements.itemMenu.descriptionText.setText(description);
            }
            
            this.elements.itemMenu.itemTexts.push(text);
        });
    }
    
    /**
     * 获取道具描述
     * @param {Object} item - 道具对象
     * @returns {string} 道具描述
     */
    getItemDescription(item) {
        if (!item) return '';
        
        let desc = item.description || '';
        
        if (item.healAmount) {
            desc += `\n恢复 ${item.healAmount} 点生命值`;
        }
        if (item.repairAmount) {
            desc += `\n修复战车 ${item.repairAmount} 点耐久`;
        }
        if (item.damage) {
            desc += `\n造成 ${item.damage} 点伤害`;
        }
        if (item.fuelAmount) {
            desc += `\n补充 ${item.fuelAmount} 点燃料`;
        }
        
        return desc || '无描述';
    }
    
    /**
     * 移动道具选择
     * @param {number} direction - 方向 (-1 上, 1 下)
     */
    moveItemSelection(direction) {
        if (this.itemList.length === 0) return;
        
        const newIndex = this.selectedItemIndex + direction;
        if (newIndex >= 0 && newIndex < this.itemList.length) {
            this.selectedItemIndex = newIndex;
            this.updateItemMenu(this.itemList);
        }
    }
    
    /**
     * 获取选中的道具
     * @returns {Object|null} 选中的道具
     */
    getSelectedItem() {
        if (this.itemList.length === 0) return null;
        return this.itemList[this.selectedItemIndex];
    }
    
    /**
     * 显示道具菜单
     */
    showItemMenu() {
        if (this.elements.itemMenu.background) {
            this.elements.itemMenu.background.setVisible(true);
        }
        if (this.elements.itemMenu.border) {
            this.elements.itemMenu.border.setVisible(true);
        }
        if (this.elements.itemMenu.title) {
            this.elements.itemMenu.title.setVisible(true);
        }
        if (this.elements.itemMenu.descriptionText) {
            this.elements.itemMenu.descriptionText.setVisible(true);
        }
        if (this.elements.itemMenu.closeArea) {
            this.elements.itemMenu.closeArea.setVisible(true);
        }
        this.elements.itemMenu.itemTexts.forEach(text => {
            if (text) text.setVisible(true);
        });
        this.itemMenuVisible = true;
    }
    
    /**
     * 隐藏道具菜单
     */
    hideItemMenu() {
        if (this.elements.itemMenu.background) {
            this.elements.itemMenu.background.setVisible(false);
        }
        if (this.elements.itemMenu.border) {
            this.elements.itemMenu.border.setVisible(false);
        }
        if (this.elements.itemMenu.title) {
            this.elements.itemMenu.title.setVisible(false);
        }
        if (this.elements.itemMenu.descriptionText) {
            this.elements.itemMenu.descriptionText.setVisible(false);
        }
        if (this.elements.itemMenu.closeArea) {
            this.elements.itemMenu.closeArea.setVisible(false);
        }
        this.elements.itemMenu.itemTexts.forEach(text => {
            if (text) text.setVisible(false);
        });
        this.itemMenuVisible = false;
    }
    
    /**
     * 重置选择状态
     */
    resetSelection() {
        this.selectingSkill = false;
        this.selectingItem = false;
        this.selectingWeapon = false;
        this.selectedOption = 0;
        this.updateOptionSelection();
    }
    
    /**
     * 创建武器菜单（战车武器子菜单）
     */
    createWeaponMenu() {
        const screenHeight = this.scene.cameras.main.height;
        const screenWidth = this.scene.cameras.main.width;
        
        // 武器菜单背景
        this.elements.weaponMenu = this.elements.weaponMenu || {};
        this.elements.weaponMenu.background = this.scene.add.graphics();
        this.elements.weaponMenu.background.fillStyle(0x1a1a2e, 0.95);
        this.elements.weaponMenu.background.fillRect(
            screenWidth * 0.3,
            screenHeight * 0.2,
            screenWidth * 0.4,
            screenHeight * 0.5
        );
        
        this.elements.weaponMenu.border = this.scene.add.graphics();
        this.elements.weaponMenu.border.lineStyle(3, 0x00ff00, 1);
        this.elements.weaponMenu.border.strokeRect(
            screenWidth * 0.3,
            screenHeight * 0.2,
            screenWidth * 0.4,
            screenHeight * 0.5
        );
        
        // 武器菜单标题
        this.elements.weaponMenu.title = this.scene.add.text(
            screenWidth * 0.5,
            screenHeight * 0.25,
            '选择武器',
            {
                fontSize: '20px',
                fill: '#00ff00',
                fontFamily: 'monospace',
                fontWeight: 'bold'
            }
        ).setOrigin(0.5);
        
        this.elements.weaponMenu.weaponTexts = [];
        
        // 创建点击空白处关闭的区域
        this.elements.weaponMenu.closeArea = this.scene.add.graphics();
        this.elements.weaponMenu.closeArea.fillStyle(0x000000, 0.01);
        this.elements.weaponMenu.closeArea.fillRect(0, 0, screenWidth, screenHeight);
        this.elements.weaponMenu.closeArea.setScrollFactor(0);
        this.elements.weaponMenu.closeArea.setDepth(9998);
        this.elements.weaponMenu.closeArea.setInteractive(
            new Phaser.Geom.Rectangle(0, 0, screenWidth, screenHeight),
            Phaser.Geom.Rectangle.Contains
        );
        this.elements.weaponMenu.closeArea.on('pointerdown', (pointer) => {
            const menuX = screenWidth * 0.3;
            const menuY = screenHeight * 0.2;
            const menuWidth = screenWidth * 0.4;
            const menuHeight = screenHeight * 0.5;
            
            const clickX = pointer.x;
            const clickY = pointer.y;
            
            if (clickX < menuX || clickX > menuX + menuWidth || 
                clickY < menuY || clickY > menuY + menuHeight) {
                this.selectingWeapon = false;
                this.hideWeaponMenu();
                if (this.scene.battleUI && this.scene.battleUI.events) {
                    this.scene.battleUI.events.emit('cancelWeaponSelection');
                }
            }
        });
        
        // 初始隐藏武器菜单
        this.hideWeaponMenu();
    }
    
    /**
     * 显示武器选择
     * @param {Array} weapons - 武器列表
     * @param {string} weaponType - 武器类型 ('mainCannon', 'subCannon', 'se')
     */
    showWeaponSelection(weapons, weaponType) {
        this.selectingWeapon = true;
        this.weaponList = weapons || [];
        this.weaponType = weaponType;
        this.selectedWeaponIndex = 0;
        
        this.updateWeaponMenu(weapons);
        this.showWeaponMenu();
    }
    
    /**
     * 更新武器菜单
     * @param {Array} weapons - 武器列表
     */
    updateWeaponMenu(weapons) {
        // 清除旧的武器文本
        if (this.elements.weaponMenu && this.elements.weaponMenu.weaponTexts) {
            this.elements.weaponMenu.weaponTexts.forEach(text => {
                if (text && text.destroy) text.destroy();
            });
        }
        
        if (!this.elements.weaponMenu) {
            this.elements.weaponMenu = {};
        }
        this.elements.weaponMenu.weaponTexts = [];
        
        if (!weapons || weapons.length === 0) {
            const noWeaponText = this.scene.add.text(
                this.scene.cameras.main.width * 0.5,
                this.scene.cameras.main.height * 0.4,
                '没有可用武器',
                {
                    fontSize: '16px',
                    fill: '#888888',
                    fontFamily: 'monospace'
                }
            ).setOrigin(0.5);
            this.elements.weaponMenu.weaponTexts.push(noWeaponText);
            return;
        }
        
        const screenWidth = this.scene.cameras.main.width;
        const screenHeight = this.scene.cameras.main.height;
        const startY = screenHeight * 0.3;
        const spacing = 35;
        
        // 显示武器列表
        weapons.forEach((weapon, index) => {
            const y = startY + (index * spacing);
            
            // 检查弹药
            const player = this.scene.battlePlayer?.getPlayer();
            const hasAmmo = player && player.vehicle ? 
                this.scene.battleLogic?.scene?.battleLogic?.hasAmmo?.(weapon, player.vehicle) : true;
            
            // 武器名称和弹药信息
            let weaponText = weapon.name;
            if (weapon.ammoType && weapon.ammoConsumption) {
                const ammoCount = player?.vehicle?.ammo?.[weapon.ammoType] || 0;
                weaponText += ` - 弹药: ${ammoCount}/${weapon.ammoConsumption}`;
            }
            if (weapon.damage) {
                weaponText += ` - 伤害: ${weapon.damage}`;
            }
            
            const textColor = hasAmmo ? '#ffffff' : '#888888';
            
            const text = this.scene.add.text(
                screenWidth * 0.35,
                y,
                weaponText,
                {
                    fontSize: '16px',
                    fill: textColor,
                    fontFamily: 'monospace'
                }
            );
            
            // 设置为可点击（移动端支持）
            if (hasAmmo) {
                text.setInteractive({ useHandCursor: true });
                text.on('pointerdown', () => {
                    // 检查是否可以交互
                    if (!this.canInteract()) {
                        return;
                    }
                    
                    this.selectedWeaponIndex = index;
                    this.updateWeaponMenu(weapons);
                    // 触发事件
                    if (this.scene.battleUI && this.scene.battleUI.events) {
                        const selectedWeapon = weapons[index];
                        this.scene.battleUI.events.emit('weaponSelected', selectedWeapon, index, this.weaponType);
                    }
                });
            }
            
            // 应用选中项高亮样式
            applySelectionHighlight(text, index === this.selectedWeaponIndex, hasAmmo);
            
            this.elements.weaponMenu.weaponTexts.push(text);
        });
    }
    
    /**
     * 显示武器菜单
     */
    showWeaponMenu() {
        if (!this.elements.weaponMenu) return;
        
        if (this.elements.weaponMenu.background) {
            this.elements.weaponMenu.background.setVisible(true);
        }
        if (this.elements.weaponMenu.border) {
            this.elements.weaponMenu.border.setVisible(true);
        }
        if (this.elements.weaponMenu.title) {
            this.elements.weaponMenu.title.setVisible(true);
        }
        if (this.elements.weaponMenu.closeArea) {
            this.elements.weaponMenu.closeArea.setVisible(true);
        }
        if (this.elements.weaponMenu.weaponTexts) {
            this.elements.weaponMenu.weaponTexts.forEach(text => {
                if (text) text.setVisible(true);
            });
        }
        this.weaponMenuVisible = true;
    }
    
    /**
     * 隐藏武器菜单
     */
    hideWeaponMenu() {
        if (!this.elements.weaponMenu) return;
        
        if (this.elements.weaponMenu.background) {
            this.elements.weaponMenu.background.setVisible(false);
        }
        if (this.elements.weaponMenu.border) {
            this.elements.weaponMenu.border.setVisible(false);
        }
        if (this.elements.weaponMenu.title) {
            this.elements.weaponMenu.title.setVisible(false);
        }
        if (this.elements.weaponMenu.closeArea) {
            this.elements.weaponMenu.closeArea.setVisible(false);
        }
        if (this.elements.weaponMenu.weaponTexts) {
            this.elements.weaponMenu.weaponTexts.forEach(text => {
                if (text) text.setVisible(false);
            });
        }
        this.weaponMenuVisible = false;
    }
    
    /**
     * 移动武器选择
     * @param {number} direction - 方向 (-1 上, 1 下)
     */
    moveWeaponSelection(direction) {
        if (this.weaponList.length === 0) return;
        
        const newIndex = this.selectedWeaponIndex + direction;
        if (newIndex >= 0 && newIndex < this.weaponList.length) {
            this.selectedWeaponIndex = newIndex;
            this.updateWeaponMenu(this.weaponList);
        }
    }
    
    /**
     * 获取选中的武器
     * @returns {Object|null} 选中的武器
     */
    getSelectedWeapon() {
        if (this.weaponList.length === 0) return null;
        return this.weaponList[this.selectedWeaponIndex];
    }
}

